import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { MessageCircle } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* Left panel - form */}
      <div className="flex items-center justify-center bg-background p-8">
        <div className="w-full max-w-sm">
          <div className="mb-10 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-fun-blue to-fun-aqua text-white shadow-md">
              <MessageCircle className="h-6 w-6" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading text-lg font-bold leading-none">Odelia CRM</div>
              <div className="mt-1 text-[11px] uppercase tracking-widest text-muted-foreground">
                WhatsApp Business
              </div>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">
            Welcome back.
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign in to manage seminars, leads and WhatsApp campaigns.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full"
              data-testid="login-submit-btn"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <Card className="mt-6 border-dashed bg-accent/40 p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              Demo credentials
            </div>
            <div className="mt-2 font-mono text-xs text-foreground/80">
              warule.manoj@gmail.com / Admin@123
            </div>
          </Card>
        </div>
      </div>

      {/* Right panel - visual */}
      <div
        className="hidden bg-cover bg-center lg:block"
        style={{
          backgroundImage:
            "linear-gradient(180deg, hsl(153 75% 15% / 0.55), hsl(153 75% 15% / 0.75)), url(https://images.unsplash.com/photo-1601642964568-1917224f4e4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHx3YXJtJTIwbWluaW1hbGlzdCUyMGRlc2slMjBuZXV0cmFsfGVufDB8fHx8MTc4NTkyNDg0NHww&ixlib=rb-4.1.0&q=85)",
        }}
      >
        <div className="flex h-full w-full flex-col justify-end p-14 text-primary-foreground">
          <div className="max-w-md">
            <div className="mb-4 inline-flex rounded-full border border-white/30 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest backdrop-blur">
              Seminar &amp; Programme Management
            </div>
            <h2 className="font-heading text-4xl font-extrabold leading-tight tracking-tight">
              From invite to attendance — every step, in one WhatsApp CRM.
            </h2>
            <p className="mt-4 text-sm text-white/80">
              Bulk template messaging, automated registration workflows, live inbox, one-tap attendance
              on event day — all tied to the programme.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
